/**
 * Utilities for parsing, encoding, and rendering @mentions in text content.
 *
 * Mention format: @[Display Name](member-uuid)
 * @everyone format: @[everyone](everyone)
 */

// Regex pattern for matching mentions
// Matches: @[Display Name](uuid-or-everyone)
const MENTION_PATTERN = /@\[([^\]]+)\]\(([a-f0-9-]+|everyone)\)/g;

export type ParsedMention = {
  /** The member's display name */
  memberName: string;
  /** The member's UUID, or "everyone" for @everyone mentions */
  memberId: string;
  /** Whether this is an @everyone mention */
  isEveryone: boolean;
  /** The full match string (e.g., "@[John](uuid)") */
  fullMatch: string;
};

/**
 * Parse mention patterns from text content.
 *
 * @example
 * parseMentions("Hey @[John Smith](abc-123) and @[everyone](everyone)!")
 * // Returns: [
 * //   { memberName: "John Smith", memberId: "abc-123", isEveryone: false, fullMatch: "@[John Smith](abc-123)" },
 * //   { memberName: "everyone", memberId: "everyone", isEveryone: true, fullMatch: "@[everyone](everyone)" }
 * // ]
 */
export function parseMentions(text: string): ParsedMention[] {
  const mentions: ParsedMention[] = [];
  // Create a new regex instance to avoid issues with global flag state
  const regex = new RegExp(MENTION_PATTERN.source, "g");

  for (let match = regex.exec(text); match !== null; match = regex.exec(text)) {
    const fullMatch = match[0];
    const memberName = match[1] ?? "";
    const memberId = match[2] ?? "";
    mentions.push({
      memberName,
      memberId,
      isEveryone: memberId === "everyone",
      fullMatch,
    });
  }

  return mentions;
}

/**
 * Encode a mention for storage in text content.
 *
 * @example
 * encodeMention("John Smith", "abc-123")
 * // Returns: "@[John Smith](abc-123)"
 */
export function encodeMention(memberName: string, memberId: string): string {
  // Sanitize the name to prevent breaking the pattern
  const safeName = memberName.replace(/[[\]()]/g, "");
  return `@[${safeName}](${memberId})`;
}

/**
 * Encode an @everyone mention for storage in text content.
 *
 * @example
 * encodeEveryoneMention()
 * // Returns: "@[everyone](everyone)"
 */
export function encodeEveryoneMention(): string {
  return "@[everyone](everyone)";
}

/**
 * Strip mentions from text, converting them to plain text display names.
 * Useful for email previews or plain text contexts.
 *
 * @example
 * stripMentions("Hey @[John Smith](abc-123)!")
 * // Returns: "Hey @John Smith!"
 */
export function stripMentions(text: string): string {
  return text.replace(MENTION_PATTERN, "@$1");
}

/**
 * Create a plain-text preview from rich text content (HTML + mentions).
 * Strips mention syntax, HTML tags, and trims whitespace.
 *
 * @example
 * plainTextPreview("<p>@[John](abc) said hello</p>", 50)
 * // Returns: "@John said hello"
 */
export function plainTextPreview(text: string, maxLength = 100): string {
  const clean = stripMentions(text)
    .replace(/<[^>]*>/g, "")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .trim();
  if (clean.length <= maxLength) return clean;
  // Truncate at the last space before maxLength to avoid cutting mid-word
  const truncated = clean.slice(0, maxLength);
  const lastSpace = truncated.lastIndexOf(" ");
  return `${lastSpace > maxLength * 0.5 ? truncated.slice(0, lastSpace) : truncated}...`;
}

/**
 * Convert rich text (HTML + mentions) to plain text suitable for email body
 * rendering. Preserves paragraph and line breaks as `\n` so emails can render
 * with `white-space: pre-wrap`. No truncation.
 */
export function richTextToPlainText(text: string): string {
  return stripMentions(text)
    .replace(/<\s*br\s*\/?\s*>/gi, "\n")
    .replace(/<\/\s*(p|div|li|h[1-6])\s*>/gi, "\n\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&#x27;/gi, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * Check if text contains an @everyone mention.
 */
export function hasEveryoneMention(text: string): boolean {
  return text.includes("@[everyone](everyone)");
}

/**
 * Get unique member IDs from mentions (excluding @everyone).
 */
export function getUniqueMemberIds(mentions: ParsedMention[]): string[] {
  const ids = new Set<string>();
  for (const mention of mentions) {
    if (!mention.isEveryone) {
      ids.add(mention.memberId);
    }
  }
  return Array.from(ids);
}

/**
 * Render mentions as HTML with styled spans.
 * Escapes the content first to prevent XSS, then renders mention patterns.
 *
 * @example
 * renderMentions("Hey @[John Smith](abc-123)!")
 * // Returns: "Hey <span class=\"text-primary font-medium\">@John Smith</span>!"
 */
export function renderMentions(text: string): string {
  // First escape HTML to prevent XSS
  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

  // Then replace mention patterns with styled spans
  return escaped.replace(
    /@\[([^\]]+)\]\(([a-f0-9-]+|everyone)\)/g,
    (_, name, id) =>
      `<span class="mention" data-mention-id="${id}" data-mention-name="${name}">@${name}</span>`,
  );
}
